// Services
import API from 'src/api'
import { OfflineStorage } from 'src/services/offline-storage'
import { isReallyOnline } from 'src/services/connectivity'

// Offline
import { newId } from './repositories/ids'
import { StorageAdapter } from './storage/StorageAdapter'
import { saveComments as saveCommentsOffline } from './domain/occurrences'
import { getTransport } from './sync/transport'
import { is2xx, throwFromResponse } from './sync/http-helpers'

/**
 * pending-comments.js: respaldo de los comentarios adicionales de la ocurrencia cuando no se pudieron
 * guardar en SQLite ni enviar (F-97).
 *
 * Con la base local abierta el comentario va por el outbox (occurrence.saveComments, fila en
 * occurrences.comments). Con la base cerrada (plugin de SQLite muerto, ISS-1009) o si esa escritura falla,
 * se manda con el PUT en línea; si ese PUT tampoco sale (sin red o error del servidor), se guarda aquí, en
 * OfflineStorage (JSON en disco / localStorage, independiente de SQLite), igual que las firmas.
 *
 * Archivo propio (pending_comments.json), NO answers.json: los comentarios son de la ocurrencia, no de un
 * ítem, y las filas de answers.json las leen el drain legacy, el overlay de reingreso y la limpieza de F-18.
 *
 * UNA entrada por ocurrencia (latest-state): guardar otra vez reemplaza el texto. Al reenviar:
 *   1. si la base volvió a abrir, el texto pasa al outbox (la maquinaria de siempre) y se quita de aquí;
 *   2. si no, y hay red, se manda con el mismo PUT comments/<occ> del camino en línea.
 * Solo se quita la entrada si no cambió mientras se enviaba (una edición más nueva se queda pendiente):
 * cada guardado lleva una revisión (`rev`) única, no un timestamp, porque dos guardados pueden caer en el
 * mismo milisegundo.
 */

const GRAPHQL_URL = API.GRAPHQL_URL
const NOT_UPDATED = 'update operation has not been updated'

const errorMessage = (error) => String((error && error.message) || error)

function withDefaults (options) {
  return {
    storage: OfflineStorage,
    transport: getTransport,
    isOnline: isReallyOnline,
    isStorageReady: () => StorageAdapter.isReady(),
    saveToOutbox: saveCommentsOffline,
    ...options
  }
}

async function readRecords (storage) {
  const stored = await storage.getPendingComments()
  return Array.isArray(stored) ? stored : []
}

// Escrituras serializadas: guardar y el replay leen y reescriben el mismo archivo.
let writeChain = Promise.resolve()

function serialized (work) {
  const run = writeChain.then(work, work)
  writeChain = run.catch(() => {})
  return run
}

/**
 * Guarda (o reemplaza) el comentario pendiente de una ocurrencia.
 * @param {object} args { occurrenceId, comments }
 * @param {object} [options] { storage }
 * @returns {Promise<void>}
 */
export function savePendingComment ({ occurrenceId, comments } = {}, options = {}) {
  const { storage } = withDefaults(options)
  return serialized(async () => {
    const records = (await readRecords(storage)).filter(r => r.occurrence_id !== String(occurrenceId))
    records.push({
      occurrence_id: String(occurrenceId),
      comments: comments ?? '',
      rev: newId(),
      updated_at: new Date().toISOString(),
      attempts: 0
    })
    await storage.setPendingComments(records)
  })
}

/**
 * Quita el comentario pendiente de una ocurrencia porque una versión más nueva ya se guardó por otro
 * camino (outbox o PUT en línea). Sin esto, el replay mandaría después el texto viejo y pisaría el nuevo.
 * @returns {Promise<void>}
 */
export function discardPendingComment (occurrenceId, options = {}) {
  const { storage } = withDefaults(options)
  return serialized(async () => {
    const records = await readRecords(storage)
    const remaining = records.filter(r => r.occurrence_id !== String(occurrenceId))
    if (remaining.length !== records.length) await storage.setPendingComments(remaining)
  })
}

/**
 * Comentario pendiente de envío de una ocurrencia (para pintarlo al reabrir).
 * @returns {Promise<{ comments: string }|null>}
 */
export async function getPendingComment (occurrenceId, options = {}) {
  const { storage } = withDefaults(options)
  const record = (await readRecords(storage)).find(r => r.occurrence_id === String(occurrenceId))
  return record ? { comments: record.comments } : null
}

async function sendOne (record, deps) {
  const res = await deps.transport().put(
    `${GRAPHQL_URL}comments/${record.occurrence_id}`,
    { id: Number(record.occurrence_id), comments: record.comments },
    { headers: { 'Idempotency-Key': `pending-comment:${record.occurrence_id}:${record.rev}` } }
  )
  if (!is2xx(res)) throwFromResponse(res)
  if (res?.data?.result === NOT_UPDATED) throwFromResponse({ ...res, status: 500 })
}

async function deliver (record, deps) {
  if (deps.isStorageReady()) {
    try {
      await deps.saveToOutbox(record.occurrence_id, record.comments)
      return true
    } catch (error) {
      console.error('[pending-comments] no se pudo pasar el comentario al outbox:', error)
    }
  }
  if (!(await deps.isOnline())) return false
  await sendOne(record, deps)
  return true
}

async function runReplay (options) {
  const deps = withDefaults(options)
  const snapshot = await readRecords(deps.storage)
  if (!snapshot.length) return { delivered: [], pending: 0 }

  const delivered = []
  for (const record of snapshot) {
    try {
      if (!(await deliver(record, deps))) continue
      delivered.push(record.occurrence_id)
    } catch (error) {
      console.error('[pending-comments] no se pudo enviar el comentario pendiente:', error)
      await serialized(async () => {
        const records = await readRecords(deps.storage)
        const current = records.find(r => r.occurrence_id === record.occurrence_id && r.rev === record.rev)
        if (!current) return
        current.attempts = (current.attempts || 0) + 1
        current.last_error = errorMessage(error)
        await deps.storage.setPendingComments(records)
      })
    }
  }

  return serialized(async () => {
    const records = await readRecords(deps.storage)
    // Solo se quita la entrada que se entregó tal cual: si el usuario la editó mientras se enviaba,
    // la versión nueva se queda para el siguiente intento.
    const remaining = records.filter(r => !delivered.includes(r.occurrence_id) ||
      !snapshot.some(s => s.occurrence_id === r.occurrence_id && s.rev === r.rev))
    if (remaining.length !== records.length) await deps.storage.setPendingComments(remaining)
    return { delivered, pending: remaining.length }
  })
}

let inFlightReplay = null

/**
 * Reenvía los comentarios pendientes. Seguro de llamar varias veces: las llamadas concurrentes comparten
 * la misma pasada. Un comentario que falla se queda pendiente para el siguiente intento.
 * @returns {Promise<{ delivered: string[], pending: number }>}
 */
export function replayPendingComments (options = {}) {
  if (inFlightReplay) return inFlightReplay
  inFlightReplay = runReplay(options).finally(() => { inFlightReplay = null })
  return inFlightReplay
}

let listenerInstalled = false

export function startPendingCommentWatcher () {
  if (listenerInstalled) return
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  listenerInstalled = true
  window.addEventListener('online', () => {
    replayPendingComments().catch(error => console.error('[pending-comments] replay fallo:', error))
  })
}

startPendingCommentWatcher()
